import React from 'react';
import _GeneralDialog from '../../../internal-components/GeneralDialog';
import styled from 'styled-components/macro';
import { Divider, FormControlLabel } from '@material-ui/core';
import useTranslations from '../../../hooks/useTranslations';
import _Chip from '@material-ui/core/Chip';
import EditableTaskFieldEngine from '../engine/EditableTaskFieldEngine';
import TaskActivityEngine from '../engine/TaskActivityEngine';
import AddTaskCommentEngine from '../engine/AddTaskCommentEngine';
import FileUploadEngine from '../../engine/FileUploadEngine';
import TaskFilesEngine from '../engine/TaskFilesEngine';

const ColumnsContainer = styled.div`
  display: flex;
  flex-direction: columns;
`;
const LeftSideContainer = styled.div`
  flex: 1;
  padding-right: 1rem;
`;
const RightSideContainer = styled.div`
  border-left: 1px solid #0000001a;
  flex: 1;
  padding-left: 1rem;
`;
const TitleDiv = styled.div`
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  margin-bottom: 1rem;
`;

const DescriptionDiv = styled.div``;

const AllFieldsContainer = styled.div``;

const FieldContainer = styled.div`
  align-items: center;
  display: flex;
  margin: 0.5rem 0;
`;

const FieldLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 19.5px;
  margin: 0.5rem 0;
  width: 140px;
`;

const AttachmentsLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 19.5px;
  margin: 0.5rem 0;
`;

const MaximumSizeLabel = styled.span`
  font-weight: 400;
`;

const FieldValue = styled.div`
  flex: 1;
`;

const GeneralDialog = styled(_GeneralDialog)`
  [class*='MuiPaper-root'] {
    max-width: 1050px;
  }
  [class*='MuiDialogContent-root'] {
    min-width: 1050px;
  }
  height: ${({ $hidden }) => ($hidden ? '0' : null)};
`;

const ControlContainer = styled.div`
  && {
    width: 650px;
  }
`;

const ControlLabel = styled(FormControlLabel)`
  && {
    align-items: start;
    margin-left: 0;
  }
`;

const FileUploadContainer = styled.div`
  && {
    margin-top: -35px;
    width: 100%;
  }
  [class*='FileUploadView__Container'] {
    width: 100%;
  }
`;

const EditTaskDialogView = props => {
  const t = useTranslations();
  return (
    <GeneralDialog
      buttons={[]}
      handleClose={props.handleClose}
      handleConfirm={props.handleConfirm}
      open={props.open}
      $hidden={props.isLoading}
    >
      <ColumnsContainer>
        <LeftSideContainer>
          <TitleDiv>
            <EditableTaskFieldEngine
              fieldId="title"
              isEditable={
                !props.isEditing && !props.endUserDialog && !props.viewOnly
              }
              isEditing={props.isEditing === 'title'}
              required
              setIsEditing={v => props.setIsEditing(v)}
              setValue={v => props.setFieldValue('title', v)}
              value={props.title}
              uiType="text"
            />
          </TitleDiv>
          <DescriptionDiv>
            <EditableTaskFieldEngine
              fieldId="description"
              isEditable={
                !props.isEditing && !props.endUserDialog && !props.viewOnly
              }
              isEditing={props.isEditing === 'description'}
              setIsEditing={v => props.setIsEditing(v)}
              setValue={v => props.setFieldValue('description', v)}
              value={props.description}
              uiType="long_text"
            />
          </DescriptionDiv>
          <Divider />
          <AllFieldsContainer>
            <FieldContainer>
              <FieldLabel>{t('tasks.dialog.status')}</FieldLabel>
              <FieldValue>
                <EditableTaskFieldEngine
                  fieldId="status"
                  isEditable={!props.isEditing && !props.viewOnly}
                  isEditing={props.isEditing === 'status'}
                  setIsEditing={v => props.setIsEditing(v)}
                  setValue={v => props.setFieldValue('status', v)}
                  value={props.status}
                  uiType="status"
                />
              </FieldValue>
            </FieldContainer>
            <FieldContainer>
              <FieldLabel>{t('tasks.dialog.priority')}</FieldLabel>
              <FieldValue>
                <EditableTaskFieldEngine
                  fieldId="priority"
                  isEditable={
                    !props.isEditing && !props.endUserDialog && !props.viewOnly
                  }
                  isEditing={props.isEditing === 'priority'}
                  setIsEditing={v => props.setIsEditing(v)}
                  setValue={v => props.setFieldValue('priority', v)}
                  value={props.priority}
                  uiType="priority"
                />
              </FieldValue>
            </FieldContainer>
            <FieldContainer>
              <FieldLabel>{t('tasks.dialog.due_date')}</FieldLabel>
              <FieldValue>
                <EditableTaskFieldEngine
                  fieldId="dueDate"
                  isEditable={
                    !props.isEditing && !props.endUserDialog && !props.viewOnly
                  }
                  isEditing={props.isEditing === 'dueDate'}
                  setIsEditing={v => props.setIsEditing(v)}
                  setValue={v => props.setFieldValue('dueDate', v)}
                  value={props.dueDate}
                  uiType="date"
                />
              </FieldValue>
            </FieldContainer>
            <FieldContainer>
              <FieldLabel>{t('tasks.dialog.assigned_to')}</FieldLabel>
              <FieldValue>
                <EditableTaskFieldEngine
                  fieldId="assigneeId"
                  isEditable={
                    !props.isEditing &&
                    (!props.endUserDialog || props.canReassign) &&
                    !props.viewOnly
                  }
                  isEditing={props.isEditing === 'assigneeId'}
                  setIsEditing={v => props.setIsEditing(v)}
                  setValue={v => props.setFieldValue('assigneeId', v)}
                  value={props.assigneeId}
                  initialDisplayValue={props.assigneeDisplay}
                  uiType="user"
                />
              </FieldValue>
            </FieldContainer>
            <FieldContainer>
              <FieldLabel>{t('tasks.dialog.created_on')}</FieldLabel>
              <FieldValue>
                <EditableTaskFieldEngine
                  fieldId="createdDate"
                  value={props.createdDate}
                  uiType="date"
                />
              </FieldValue>
            </FieldContainer>
            <FieldContainer>
              <FieldLabel>{t('tasks.dialog.created_by')}</FieldLabel>
              <FieldValue>
                <EditableTaskFieldEngine
                  fieldId="authorId"
                  value={props.authorId}
                  initialDisplayValue={props.authorDisplay}
                  uiType="user"
                />
              </FieldValue>
            </FieldContainer>
          </AllFieldsContainer>
          <Divider />
          <AttachmentsLabel>
            {t('tasks.dialog.attachments')}{' '}
            <MaximumSizeLabel>(Maximum size: 2 MB)</MaximumSizeLabel>
          </AttachmentsLabel>
          <ControlContainer>
            {!props.viewOnly && (
              <FileUploadContainer>
                <FileUploadEngine
                  key={'file-count' + props.existingFiles.length}
                  onFileDropped={props.onFileDropped}
                  existingFiles={props.existingFiles}
                />
              </FileUploadContainer>
            )}
          </ControlContainer>
          <TaskFilesEngine
            taskId={props.taskId}
            viewOnly={props.viewOnly}
            refreshTimeline={props.refreshTimeline}
          />
        </LeftSideContainer>
        <RightSideContainer>
          <TitleDiv>Activity</TitleDiv>
          <TaskActivityEngine
            taskId={props.taskId}
            timelineReadyCallback={props.timelineReadyCallback}
            isEndUser={props.endUserDialog}
          />
          {!props.viewOnly && (
            <AddTaskCommentEngine
              taskId={props.taskId}
              onAddedComment={props.onAddedComment}
              isEndUser={props.endUserDialog}
            />
          )}
        </RightSideContainer>
      </ColumnsContainer>
    </GeneralDialog>
  );
};

export default EditTaskDialogView;
